import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import { ButtonState } from '@delmonicos/shared-design-system-react/src/components/button/button';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { Tooltip } from '@delmonicos/shared-design-system-react/src/components/tooltip/tooltip';
import { GatewayNetworkModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/networks/models/outputs/gateway-network.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { useCpmsBoGetNetworkQuery } from '@app/core/service/cpms-bo-network.service';
import { NetworkUpdateController } from '@app/feature/network/network-update/network-update.controller';
import { useSetPageTitleContext } from '@app/shared/layout/page-layout/page-layout-title.context-provider';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';

import { NetworkContextProvider } from '../network.context';
import { NetworkDetailsView } from './network-details.view';

interface NetworkDetailsControllerProps {
  resource: GatewayNetworkModel;
}

const NetworkDetailsController = (
  props: NetworkDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.name);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.NETWORKS,
        action: ActionsByResource.NETWORKS.UPDATE,
      })
    ) {
      if (resource?.isDefault) {
        actions.push(
          <Tooltip
            content={t('$.network-update.disabled')}
            key="update-network">
            <ButtonIcon state={ButtonState.Disabled} icon="edit-05" />
          </Tooltip>,
        );
      } else {
        actions.push(
          <ButtonIcon
            key="update-network"
            icon="edit-05"
            onClick={() => setActionUpdateIsDialogOpen(true)}
          />,
        );
      }
    }
    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.NETWORKS,
        action: ActionsByResource.NETWORKS.DELETE,
      })
    ) {
      actions.push(
        <ButtonIcon
          key="delete-network"
          icon="trash-01"
          onClick={() => setActionUpdateIsDialogOpen(true)}
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions, resource, t]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  // Nothing to do

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <NetworkContextProvider data={resource}>
      <NetworkDetailsView />
      <Dialog
        open={actionUpdateIsDialogOpen}
        size={DialogSize.LARGE}
        height="100%"
        confirmClose
        onClose={() => setActionUpdateIsDialogOpen(false)}>
        <Dialog.Header title={t('$.network-update.title')} icon="server-06" />
        <NetworkUpdateController
          onSuccess={() => setActionUpdateIsDialogOpen(false)}
          onCancel={() => setActionUpdateIsDialogOpen(false)}
        />
      </Dialog>
    </NetworkContextProvider>
  );
};

export const NetworkDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetNetworkQuery(params.id ?? '');

  const { Component } = withProtectedResource<
    GatewayNetworkModel,
    {},
    GatewayNetworkModel
  >(NetworkDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.NETWORKS,
        action: ActionsByResource.NETWORKS.READ,
      }),
  })({});

  return Component;
};
